import React, {memo, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  IconButton,
  Image,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  lockBodyScrollFreeze,
  unlockBodyScrollFreezeAndRestore,
} from '@amblique/king-living/app/utils/utils';

/** 17×17 viewBox; rendered size from theme (16.166px) */
const ImageZoomCloseIcon = (props) => (
  <svg
    aria-hidden
    fill="none"
    focusable="false"
    viewBox="0 0 17 17"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14.8857 0.219521C15.1785 -0.0731391 15.6534 -0.0731036 15.9462 0.219521C16.2387 0.512389 16.2388 0.987281 15.9462 1.28007L9.14345 8.08285L15.9462 14.8856C16.2387 15.1785 16.2388 15.6534 15.9462 15.9462C15.6534 16.2389 15.1786 16.2387 14.8857 15.9462L8.0829 9.14341L1.28012 15.9462C0.987334 16.2389 0.512466 16.2387 0.219567 15.9462C-0.0730882 15.6533 -0.0731849 15.1785 0.219567 14.8856L7.02235 8.08285L0.219567 1.28007C-0.0731668 0.987192 -0.0732111 0.512376 0.219567 0.219521C0.512429 -0.0731653 0.987269 -0.0731821 1.28012 0.219521L8.0829 7.0223L14.8857 0.219521Z"
      fill="#32312F"
    />
  </svg>
);

/**
 * Portal lightbox.
 * - Complex PDP (default): mobile full viewport; desktop band below header.
 * - Simple PDP (`imageZoomFullScreen`): full viewport on all breakpoints.
 * Pass `desktopLightboxRightInsetPx` (e.g. config panel width) so the stage clears the fixed panel.
 *
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} props.imageSrc
 * @param {string} [props.imageAlt]
 * @param {boolean} [props.imageZoomFullScreen=false] - Simple PDP only: cover header and lock scroll.
 * @param {Function} [props.onCloseComplete] - Called when `isOpen` becomes false (e.g. clear src in parent).
 */
const ImageZoomModalView = function ImageZoomModalView({
  desktopLightboxRightInsetPx = 0,
  imageAlt = '',
  imageSrc,
  imageZoomFullScreen = false,
  isOpen,
  onClose,
  onCloseComplete,
}) {
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const wasOpenRef = useRef(false);
  const savedScrollYRef = useRef(0);
  const styles = useMultiStyleConfig('ImageZoomModal', {
    variant: imageZoomFullScreen ? 'simplePdp' : undefined,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      onCloseComplete?.();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, onCloseComplete]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    document.body.dataset.imageZoomOpen = 'true';

    if (imageZoomFullScreen) {
      savedScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0;
      document.body.dataset.imageZoomSimplePdp = 'true';
      document.documentElement.dataset.imageZoomSimplePdp = 'true';
      lockBodyScrollFreeze(savedScrollYRef.current);
    }

    return () => {
      delete document.body.dataset.imageZoomOpen;

      if (imageZoomFullScreen) {
        delete document.body.dataset.imageZoomSimplePdp;
        delete document.documentElement.dataset.imageZoomSimplePdp;
        unlockBodyScrollFreezeAndRestore(savedScrollYRef.current);
      }
    };
  }, [imageZoomFullScreen, isOpen]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleInnerClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const closeLabel = intl.formatMessage({
    defaultMessage: 'Close',
    id: 'image_zoom_modal.close',
  });

  const dialogLabel = intl.formatMessage({
    defaultMessage: 'Enlarged image',
    id: 'image_zoom_modal.dialog_label',
  });

  if (!mounted || !isOpen || typeof document === 'undefined') {
    return null;
  }

  const stageSx =
    desktopLightboxRightInsetPx > 0 ? {lg: {right: `${desktopLightboxRightInsetPx}px`}} : undefined;

  return createPortal(
    <Box
      {...styles.root}
      aria-label={dialogLabel}
      aria-modal={false}
      className="image-zoom-modal"
      role="dialog"
    >
      <Box className="image-zoom-modal__backdrop" {...styles.backdrop} onClick={handleBackdropClick} />
      <Box className="image-zoom-modal__stage" {...styles.stage} sx={stageSx}>
        <Box className="image-zoom-modal__inner" {...styles.inner} onClick={handleInnerClick}>
          {imageSrc ? (
            <Image
              alt={imageAlt}
              loading="eager"
              src={imageSrc}
              {...styles.image}
            />
          ) : null}
          <IconButton
            aria-label={closeLabel}
            icon={<ImageZoomCloseIcon />}
            variant="unstyled"
            {...styles.closeButton}
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
          />
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

ImageZoomModalView.propTypes = {
  desktopLightboxRightInsetPx: PropTypes.number,
  imageAlt: PropTypes.string,
  imageSrc: PropTypes.string,
  imageZoomFullScreen: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseComplete: PropTypes.func,
};

const ImageZoomModal = memo(ImageZoomModalView);

ImageZoomModal.displayName = 'ImageZoomModal';

export default ImageZoomModal;

/**
 * Local state helper for image zoom (optional).
 *
 * @returns {{
 *   close: () => void,
 *   handleCloseComplete: () => void,
 *   imageAlt: string,
 *   imageSrc: string,
 *   isOpen: boolean,
 *   open: (src: string, alt?: string) => void
 * }}
 */
export function useImageZoomModalState() {
  const [isOpen, setIsOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [imageAlt, setImageAlt] = useState('');

  const open = useCallback((src, alt = '') => {
    if (!src) {
      return;
    }

    setImageSrc(src);
    setImageAlt(alt);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCloseComplete = useCallback(() => {
    setImageSrc('');
    setImageAlt('');
  }, []);

  return {
    close,
    handleCloseComplete,
    imageAlt,
    imageSrc,
    isOpen,
    open,
  };
}
